import { ClientAdministrationRow } from "@/data/models/ClientAdministrationRow";

/**
 * Changes made on a client's detail that its row in the Mandanten list shows. The list is kept alive
 * and does not fetch its rows again on return (`ClientAdministrationList.vue`), so without this a
 * change such as "Kein Echtbetrieb" (ticket 2610043) stayed invisible there until Aktualisieren
 * (Taimoor, 2026-10-13: update that one row rather than reload the list).
 *
 * Plain module state, not reactive: it is written by a dialog and read once by the list's `activated`
 * hook. A fresh load supersedes everything queued, so the list clears it after each load.
 */
const pending = new Map<string, Partial<ClientAdministrationRow>>();

/** Queues fields of one client's row; a later update of the same field wins. */
export function queueClientRowUpdate(clientId: string, patch: Partial<ClientAdministrationRow>): void {
  pending.set(clientId, { ...pending.get(clientId), ...patch });
}

/** Whether any update is waiting, so the list leaves its rows alone otherwise. */
export function hasQueuedClientRowUpdates(): boolean {
  return pending.size > 0;
}

/** Drops every queued update: called after the list loaded fresh rows. */
export function clearQueuedClientRowUpdates(): void {
  pending.clear();
}

/**
 * The rows with every queued update applied, and the queue emptied. Pure apart from the queue: the
 * input rows are not modified, and a row without an update is returned as it was.
 */
export function applyQueuedClientRowUpdates(rows: ClientAdministrationRow[]): ClientAdministrationRow[] {
  const updated = rows.map((row) => {
    const patch = pending.get(row.id);
    return patch ? { ...row, ...patch, id: row.id } : row;
  });

  pending.clear();
  return updated;
}

/**
 * A client was created (the guided setup, #2610082), which no queued row update can add: the list
 * fetches its rows again on return, in the background, without its loading state.
 */
let reloadRequested = false;

/** Asks the list for a fresh set of rows the next time it is shown. */
export function requestClientListReload(): void {
  reloadRequested = true;
}

/** Whether a fresh set of rows was asked for; asking again needs a new request. */
export function takeClientListReload(): boolean {
  const requested = reloadRequested;
  reloadRequested = false;
  return requested;
}
